import React from 'react';
import { classNames } from '../utils/classNames';

// A list of choices in a column, one of them selected: the left side of an editor with a list and a detail (the groups of
// an editor, the detail of the selected group on the right). Each choice has a title and an optional second line; the
// selected choice is blue, with the look of a choice of TitleSelect. Each choice is a button: Tab reaches it, and
// Enter or Space selects it. The caller owns the selection: onChange receives the id of the clicked choice.

export type SelectListOption = {
	id: string,
	label: string,
	// The second line of the choice, gray ("18 properties").
	detail?: string,
};

type Props = {
	options: SelectListOption[],
	// The id of the selected choice; null: no choice is selected (for example a new item in the detail).
	value: string | null,
	onChange: (id: string) => void,
	// The name of the list for a screen reader ("Property groups").
	ariaLabel: string,
	// The text when the list has no choice ("No group yet").
	empty?: string,
};

const SelectList: React.FunctionComponent<Props> = ({ options, value, onChange, ariaLabel, empty }) => {
	if (options.length === 0) return empty === undefined ? null : <p className="px-3 py-1.5 text-sm text-gray-500">{empty}</p>;
	return <ul aria-label={ariaLabel} className="flex flex-col gap-1">
		{options.map(option => {
			const selected = option.id === value;
			return <li key={option.id}>
				<button type="button" aria-current={selected ? 'true' : undefined} onClick={() => onChange(option.id)}
					className={classNames('flex w-full min-w-0 flex-col items-start rounded-md px-3 py-1.5 text-left focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600',
						selected ? 'bg-blue-50' : 'hover:bg-gray-50')}>
					<span className={classNames('block max-w-full truncate text-sm font-semibold', selected ? 'text-blue-700' : 'text-gray-900')}>{option.label}</span>
					{option.detail !== undefined && <span className="block max-w-full truncate text-xs text-gray-500">{option.detail}</span>}
				</button>
			</li>;
		})}
	</ul>;
};

export default SelectList;
